import { useRef, useState } from 'react';
import { TruncatedText } from '@/shared/components/TruncatedText';
import { TEMPLATE_FILE_MAX_BYTES, fileNameOf, hasFileFormat } from '../../models/templateFile';
import type { TemplateFileFormats } from '../../models/templateFile';

/**
 * A file as the answer the server stores: a data URI carrying the file's name --
 * `data:application/pdf;name=quote.pdf;base64,...`.
 */
function readAsAnswer(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = typeof reader.result === 'string' ? reader.result : '';
      const comma = dataUrl.indexOf(',');
      const head = dataUrl.slice(0, comma);
      /* `data:<type>;base64` -- an unknown type is read as a plain binary file. */
      const type = /^data:([^;,]+)/.exec(head)?.[1] ?? 'application/octet-stream';
      resolve(`data:${type};name=${encodeURIComponent(file.name)};base64,${dataUrl.slice(comma + 1)}`);
    };
    reader.onerror = () => {
      reject(reader.error ?? new Error('The file could not be read.'));
    };
    reader.readAsDataURL(file);
  });
}

interface TemplateFileInputProps {
  id: string;
  label: string;
  /** The answer: a data URI with the file's name, or '' when nothing is attached. */
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  invalid?: boolean;
  describedBy?: string;
  /**
   * The formats the file must be in: the picker offers only those, the hint names them, and any other file is
   * refused with an error and not attached. Absent, any format is taken (every form but a Purchase Request).
   */
  formats?: TemplateFileFormats;
}

/**
 * A template's File upload field: the masters' upload control (`ms-upload`, as Entity Logo and Login Image draw
 * it) -- a "Click to upload file" zone, then the attached file as a chip with its name, a download link and, while
 * editable, a remove control. The answer is the file itself, read as a data URI with its name, at most 5 MB, and
 * in one of `formats` where those are given.
 */
export function TemplateFileInput({
  id,
  label,
  value,
  onChange,
  disabled = false,
  invalid = false,
  describedBy,
  formats,
}: TemplateFileInputProps) {
  const input = useRef<HTMLInputElement>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const attached = value.trim() !== '';
  const name = attached ? fileNameOf(value) : '';

  return (
    <div className={`ms-upload${invalid ? ' is-invalid' : ''}`}>
      <input
        ref={input}
        id={id}
        type="file"
        className="ms-upload__input"
        disabled={disabled}
        aria-invalid={invalid}
        aria-describedby={describedBy}
        accept={formats?.extensions.map((extension) => `.${extension}`).join(',')}
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (!file) return;
          /* The picker's filter is a suggestion -- "All files" bypasses it -- so the name is checked as well. */
          if (formats && !hasFileFormat(file.name, formats)) {
            setProblem(`${label} must be a ${formats.names} file.`);
            return;
          }
          if (file.size > TEMPLATE_FILE_MAX_BYTES) {
            setProblem(`${label} must be 5 MB or smaller.`);
            return;
          }
          setProblem(null);
          readAsAnswer(file).then(onChange, () => {
            setProblem(`${label} could not be read. Try a different file.`);
          });
        }}
      />

      {!disabled || !attached ? (
        <button
          type="button"
          className={`ms-upload__zone${disabled ? ' ms-upload__zone--readonly' : ''}`}
          disabled={disabled}
          aria-label={`Upload ${label}`}
          onClick={() => {
            input.current?.click();
          }}
        >
          <span className="ms-upload__badge" aria-hidden="true">
            <i className="pi pi-cloud-upload" />
          </span>
          <span className="ms-upload__text">
            <span className="ms-upload__title">{attached ? 'Click to replace file' : 'Click to upload file'}</span>
            <span className="ms-upload__hint">
              {formats?.hint ?? 'Supported formats: PDF, DOC/DOCX, XLS/XLSX, CSV, JPG, PNG, etc.'}
            </span>
          </span>
        </button>
      ) : null}

      {attached ? (
        <div className="ms-upload__chip-row">
          <span className="ms-upload__chip">
            {/* A long name ends in an ellipsis; hovering it shows the whole name. */}
            <TruncatedText text={name} className="ms-upload__chip-name" />
            <a
              className="ms-upload__chip-view"
              href={value}
              download={name}
              aria-label={`Download ${name}`}
              title={`Download ${name}`}
            >
              <i className="pi pi-download" aria-hidden="true" />
            </a>
            {!disabled ? (
              <button
                type="button"
                className="ms-upload__chip-remove"
                aria-label={`Remove ${name}`}
                title={`Remove ${name}`}
                onClick={() => {
                  setProblem(null);
                  onChange('');
                }}
              >
                <i className="pi pi-times" aria-hidden="true" />
              </button>
            ) : null}
          </span>
        </div>
      ) : null}

      {problem ? (
        <small className="ms-field__error" role="alert">
          {problem}
        </small>
      ) : null}
    </div>
  );
}
